import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import {
  createQuarterTorusGeometry,
  planeGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import { GeckoMeasurement, GeckoMeasurementUnit, GeckoPipeElbow } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Pipe, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to pipes.
 * @param model               The pipe section component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const PipeElbowSurface3D: React.FC<
  { model: GeckoPipeElbow } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, rotation, position } = React.useMemo(() => {
    const { width, height } = model.componentBounds
    const scale = extractScale(groupProps.scale).multiply(
      show3D ? new Vector3(1, 1, 1) : new Vector3(width.num, height.num, 1)
    )
    const rotation: [number, number, number] = show3D
      ? [0, 0, 0]
      : [0, 0, -Math.PI / 2]
    // Plane (0–1) is scaled then rotated -90° about Z, so its center in local space
    // is (height/2, -width/2). Set position so that point maps to (width/2, height/2)
    // where the 2D camera looks.
    const position: [number, number, number] = show3D
      ? [0, 0, 0]
      : [width.num / 2 - height.num / 2, height.num / 2 + width.num / 2, 0]

    return {
      bounds: model.componentBounds,
      scale,
      rotation,
      position,
    }
  }, [model, show3D, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return createQuarterTorusGeometry(
      model.majorRadius.num, // Distance from center to tube center
      model.radius.num, // Tube radius
      model.angle // Angle in degrees
    )
  }, [show3D, model.majorRadius.num, model.radius.num, model.angle])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.start.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      const phi = (relX * (model.angle * Math.PI)) / 180
      const R = model.majorRadius.num
      const r = model.radius.num
      return [(R + r) * Math.cos(phi), (R + r) * Math.sin(phi), 0]
    },
    [show3D, bounds, model.majorRadius.num, model.radius.num, model.angle]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      const theta = relY * 2 * Math.PI
      const R = model.majorRadius.num
      const r = model.radius.num
      return [R + r * Math.cos(theta), 0, r * Math.sin(theta)]
    },
    [show3D, bounds, model.majorRadius.num, model.radius.num]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      position={position}
      scale={scale}
      rotation={rotation}>
      {!show3D && (
        <>
          <TankDecorGroup3D>
            <Ruler3D
              anchor={'bottom'}
              start={model.componentBounds.start.y}
              end={model.componentBounds.end.y}
              includeTicks
              includeStart
              includeEnd
              getTickPosition={positionYTick}
            />
          </TankDecorGroup3D>
          <TankDecorGroup3D>
            <Ruler3D
              anchor={'right'}
              start={model.componentBounds.start.x}
              end={model.componentBounds.end.x}
              includeTicks
              includeStart
              includeEnd
              getTickPosition={positionXTick}
            />
          </TankDecorGroup3D>
        </>
      )}
    </ModelSurface3D>
  )
}
